import './Pagination.scss';
import { Table } from '@tanstack/react-table';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Props for the Pagination component
 */
type PaginationProps<T> = {
  /** The table instance from @tanstack/react-table */
  table: Table<T>;
  /** Whether to use responsive layout */
  responsive?: boolean;
};

/**
 * @description Pagination component for tables.
 * Provides navigation controls, page input, and page size selection for table data.
 *
 * @SADtrace SAD-003-2.1, SAD-003-3.1, SAD-003-4.1, SAD-003-5.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {PaginationProps<T>} props Component props
 *
 * @returns {JSX.Element} Rendered pagination controls
 */
function Pagination<T>({ table, responsive }: PaginationProps<T>) {
  const { t } = useTranslation();

  let pageIndex = Number(table.getState().pagination.pageIndex);

  const inputValue = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputValue.current)
      inputValue.current.value = (pageIndex + 1).toString();
  }, [pageIndex]);

  if (pageIndex < 0) {
    pageIndex = 0;
  }

  /**
   * @description Prevents entering minus, period, or comma characters in the page input
   *
   * @SADtrace SAD-003-2.1, SAD-003-3.1, SAD-003-4.1, SAD-003-5.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {React.KeyboardEvent<HTMLInputElement>} event Keyboard event
   *
   * @returns {void}
   */
  const preventMinus = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Minus', 'Period', 'Comma'].includes(event.code)) {
      event.preventDefault();
    }
  };

  /**
   * @description Updates the table's page index based on input value.
   * Handles validation and edge cases like empty values, out of range pages, and duplicate values.
   *
   * @SADtrace SAD-003-2.1, SAD-003-3.1, SAD-003-4.1, SAD-003-5.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>} event Focus or keyboard event
   *
   * @returns {void}
   */
  const modifyTablePageState = (
    event:
      | React.FocusEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLInputElement>,
  ) => {
    // v8 ignore start -- WAIVER(DEFENSIVE): keyboard and blur events originate from the bound page input, so the target is always an HTMLInputElement; guard retained as type-narrowing fail-safe
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }
    // v8 ignore stop -- WAIVER(DEFENSIVE)

    const page = Number(event.target.value) - 1;

    if (page >= table.getPageCount()) {
      // v8 ignore start -- WAIVER(DEFENSIVE): the ref is bound to the rendered page input, so it is never null while this handler can fire; null side kept as a fail-safe
      if (inputValue.current) {
        // v8 ignore stop -- WAIVER(DEFENSIVE)
        inputValue.current.value = (pageIndex + 1).toString();
      }

      return;
    }

    // If page is the same as the current page, do nothing
    if (pageIndex === page) return;

    // If blank reset input to defaultValue
    if ((event.target.value === '' || page < 0) && inputValue.current)
      return (inputValue.current.value = (pageIndex + 1).toString());

    // v8 ignore start -- WAIVER(DEFENSIVE): false arm unreachable — blank input and negative pages already returned through the preceding guard, so page is always >= 0 here
    if (page >= 0) {
      // v8 ignore stop -- WAIVER(DEFENSIVE)
      table.setPageIndex(page);
    }
  };

  /**
   * @description Handles Enter key press in the page input.
   * Calls modifyTablePageState when Enter is pressed to jump to the specified page.
   *
   * @SADtrace SAD-003-2.1, SAD-003-3.1, SAD-003-4.1, SAD-003-5.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {React.KeyboardEvent<HTMLInputElement>} event Keyboard event
   *
   * @returns {void}
   */
  const handlePageEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      modifyTablePageState(event);
    }
  };

  return (
    <div className={'d-flex justify-content-between dl-pagination flex-nowrap'}>
      <div className="dl-pagination__previous">
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          {t('pagination.previous')}
        </button>
      </div>

      <div className={'dl-pagination__center'}>
        {pageIndex >= 0 && table.getPageCount() > 0 && (
          <span className="dl-pagination__page-info">
            {responsive
              ? t('pagination.responsive.page')
              : t('pagination.go_to')}
            <div className="dl-pagination__page-jump">
              <input
                aria-label="jump to page"
                type="number"
                min={1}
                ref={inputValue}
                defaultValue={pageIndex + 1}
                onKeyDown={preventMinus}
                onKeyUp={handlePageEnter}
                onBlur={modifyTablePageState}
              />
            </div>
            <span className="dl-pagination__total-pages">
              {responsive ? (
                t('pagination.responsive.of') + table.getPageCount()
              ) : (
                <>
                  &nbsp;|{' '}
                  {t('pagination.page_number', {
                    value: pageIndex + 1,
                    total: table.getPageCount(),
                  })}
                </>
              )}
            </span>
          </span>
        )}
        {!responsive && pageIndex >= 0 && table.getPageCount() > 0 && (
          <span className="dl-pagination__select-wrap">
            <select
              aria-label="rows per page"
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
                table.setPageIndex(0);
              }}
            >
              {[10, 20, 50, 100].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize} {t('pagination.rows')}
                </option>
              ))}
            </select>
          </span>
        )}
      </div>

      <div className="dl-pagination__next">
        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          {t('pagination.next')}
        </button>
      </div>
    </div>
  );
}

export default Pagination;
